/**
 * Role3Facility.js
 * PSP/CRE Tool v2.0 - Medical Facility Models
 * 
 * Role 3 Facility - Combat Support Hospital (CSH) / Field Hospital
 * Comprehensive surgical capability with ICU and specialized care
 * 
 * Capabilities:
 * - All Role 2 capabilities PLUS:
 * - Definitive surgical care
 * - ICU with ventilator support
 * - Advanced imaging (CT, ultrasound)
 * - Full laboratory services
 * - Blood bank with component therapy
 * - Specialized surgery (orthopedic, neurosurgery, vascular)
 * - Hold time: Up to 30 days
 * - Surgical teams: 8-12 teams
 * 
 * NATO Standard Position: 80-100 km behind FEBA
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 26, 2024
 * Version: 2.0 - Phase 2 Evacuation Asset Architecture
 * 
 * PHASE 2 CHANGES:
 * - Added corps-level evacuation asset pool (60 ambulances, 20 helicopters)
 * - Added strategic air evacuation coordination
 * - Added PULL from Role 2E, PUSH to Role 4
 * - Added helicopter pool management for subordinate units
 */

import { BaseMedicalFacility, EVACUATION_METHODS } from './BaseMedicalFacility.js';

// ============================================================================
// ROLE 3 ORGANIC ASSET STANDARDS
// ============================================================================

/**
 * Default organic assets for Role 3 (Corps-level CSH)
 * 
 * Role 3 controls corps-level medical evacuation including:
 * - 60 ground ambulances (corps medical battalion)
 * - 20 MEDEVAC helicopters (allocated from Air Force/CAB)
 * - Strategic air evacuation coordination
 */
export const ROLE3_ORGANIC_ASSETS = {
  STANDARD: {
    ground_ambulances: {
      wheeled_light: 30,
      wheeled_armored: 25,
      tracked: 5
    },
    helicopters: {
      allocated: 12,     // Organic MEDEVAC company/flight
      on_call: 8         // Additional from Air Force/allied
    },
    personnel: {
      medics: 80,
      drivers: 60,
      litter_bearers: 60,
      flight_medics: 24
    }
  },
  
  US_CORPS: {
    // US Army Corps - larger helicopter allocation
    ground_ambulances: {
      wheeled_light: 25,
      wheeled_armored: 30,
      tracked: 10
    },
    helicopters: {
      allocated: 18,     // Larger CAB MEDEVAC element
      on_call: 12
    },
    personnel: {
      medics: 90,
      drivers: 65,
      litter_bearers: 70,
      flight_medics: 36
    }
  },
  
  ROMANIAN_CORPS: {
    // Romanian Army Corps - Air Force MEDEVAC support
    ground_ambulances: {
      wheeled_light: 30,
      wheeled_armored: 20,
      tracked: 5
    },
    helicopters: {
      allocated: 6,      // IAR-330 Puma MEDEVAC
      on_call: 4         // Air Force support
    },
    personnel: {
      medics: 70,
      drivers: 55,
      litter_bearers: 50,
      flight_medics: 12
    }
  }
};

// ============================================================================
// ROLE 3 FACILITY CLASS
// ============================================================================

export class Role3Facility extends BaseMedicalFacility {
  /**
   * Create a Role 3 facility
   * 
   * @param {Object} config - Facility configuration
   * @param {string} config.facility_id - Unique ID
   * @param {string} config.facility_name - Human-readable name
   * @param {string} config.parent_unit_id - Parent corps ID
   * @param {string} [config.national_standard='STANDARD'] - National standard
   * @param {Array} [config.supported_role2es=[]] - Role 2Es this supports
   * @param {number} [config.position_feba_km=80] - Distance from FEBA
   * @param {number} [config.beds=200] - Number of beds
   * @param {number} [config.surgical_teams=10] - Surgical teams
   * @param {number} [config.icu_beds=40] - ICU beds
   */
  constructor(config) {
    const nationalStandard = config.national_standard || 'STANDARD';
    const organicAssets = config.organic_assets || 
                          ROLE3_ORGANIC_ASSETS[nationalStandard] || 
                          ROLE3_ORGANIC_ASSETS.STANDARD;
    
    const supportedRole2Es = config.supported_role2es || [];
    
    super({
      ...config,
      role_level: 3,
      facility_type: 'ROLE3',
      parent_unit_type: 'CORPS',
      beds: config.beds || 200,
      surgical_teams: config.surgical_teams || 10,
      icu_beds: config.icu_beds || 40,
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 80,
      organic_assets: organicAssets,
      
      // Phase 2: Role 3 evacuation config
      evacuation_config: config.evacuation_config || {
        incoming_method: EVACUATION_METHODS.PULL,         // Pulls from Role 2Es
        outgoing_method: 'AUTO',         // PUSH for strategic evac, PULL for Role 4
        picks_up_from: supportedRole2Es,
        delivers_to: null,               // Role 4 (strategic) - set by force structure
        helicopter_priority: 'HIGH',     // Role 3 has high priority for helicopters
        max_hold_time_hours: 720,        // 30 days
        triggers: {
          capacity_threshold: 60,
          time_threshold_hours: 336,     // 14 days
          t1_immediate_evac: false       // T1 should be stabilized by now
        }
      }
    });
    
    // Role 3 specific properties
    this.parent_corps_id = config.parent_corps_id || config.parent_unit_id;
    this.national_standard = nationalStandard;
    this.hold_time_hours = 720;                   // 30 days
    this.can_perform_surgery = true;
    this.can_perform_definitive_surgery = true;
    this.specialized_care = true;
    
    // Enhanced surgical capacity
    this.surgical_capacity = {
      max_surgeries_per_day: this.surgical_teams * 6,
      surgeries_today: 0,
      surgical_backlog: 0,
      specialized_teams: {
        orthopedic: Math.floor(this.surgical_teams * 0.3),
        neurosurgery: Math.floor(this.surgical_teams * 0.2),
        vascular: Math.floor(this.surgical_teams * 0.2),
        general: Math.ceil(this.surgical_teams * 0.3)
      }
    };
    
    // ICU capability
    this.icu_capability = {
      total_icu_beds: this.icu_beds,
      icu_beds_occupied: 0,
      ventilators: Math.floor(this.icu_beds * 0.8),
      ventilators_in_use: 0,
      can_provide_advanced_life_support: true
    };
    
    // Large blood bank
    this.blood_bank = config.blood_bank || {
      prbc_units: 500,
      ffp_units: 500,
      platelet_units: 200,
      cryoprecipitate_units: 100,
      walking_blood_bank_activated: false,
      massive_transfusion_protocol_available: true
    };
    
    // Advanced imaging
    this.imaging = {
      has_ct_scanner: true,
      has_ultrasound: true,
      has_xray: true,
      has_fluoroscopy: true
    };
    
    // Full laboratory
    this.laboratory = {
      has_full_laboratory: true,
      can_perform_blood_typing: true,
      can_perform_microbiology: true,
      can_perform_pathology: true
    };
    
    // Supported Role 2 Enhanced facilities
    this.supported_role2es = supportedRole2Es;
    
    // Strategic air evacuation tracking
    this.strategic_evac = {
      fixed_wing_available: config.fixed_wing_available || false,
      strategic_evac_pending: 0,
      strategic_evac_completed: 0
    };
  }
  
  // ============================================================================
  // OVERRIDE: GET DEFAULT ORGANIC ASSETS
  // ============================================================================
  
  _getDefaultOrganicAssets() {
    return ROLE3_ORGANIC_ASSETS[this.national_standard] || ROLE3_ORGANIC_ASSETS.STANDARD;
  }
  
  // ============================================================================
  // PHASE 2: CORPS-LEVEL HELICOPTER POOL MANAGEMENT
  // ============================================================================
  
  /**
   * Manage corps helicopter pool allocation
   * Role 3 coordinates helicopter resources for entire corps
   * 
   * @returns {Object} Helicopter pool status
   */
  getHelicopterPoolStatus() {
    const effective = this.getEffectiveAssets();
    
    return {
      total_allocated: effective.helicopters.allocated,
      total_on_call: effective.helicopters.on_call,
      total_available: effective.helicopters.total,
      missions_flown_today: this.stats.air_evac_missions,
      supported_divisions: this.supported_role2es.length,
      pool_status: effective.helicopters.total > 10 ? 'ADEQUATE' : 
                   effective.helicopters.total > 5 ? 'MARGINAL' : 'CRITICAL'
    };
  }
  
  /**
   * Allocate helicopters from corps pool to divisions
   * 
   * @param {Object} allocation - Map of division_id -> helicopter count
   * @returns {Object} Allocation result
   */
  allocateHelicoptersToCommands(allocation) {
    const effective = this.getEffectiveAssets();
    const totalAvailable = effective.helicopters.total;
    const totalRequested = Object.values(allocation).reduce((sum, n) => sum + n, 0);
    
    // Reserve 20% for corps direct missions
    const corpsReserve = Math.ceil(totalAvailable * 0.2);
    const allocatable = totalAvailable - corpsReserve;
    
    if (totalRequested > allocatable) {
      // Proportionally reduce allocations
      const reductionFactor = allocatable / totalRequested;
      const adjustedAllocation = {};
      
      for (const [divId, count] of Object.entries(allocation)) {
        adjustedAllocation[divId] = Math.floor(count * reductionFactor);
      }
      
      return {
        success: true,
        warning: 'ALLOCATION_REDUCED',
        requested: totalRequested,
        available: allocatable,
        reduction_factor: reductionFactor,
        allocations: adjustedAllocation,
        corps_reserve: corpsReserve
      };
    }
    
    return {
      success: true,
      allocations: allocation,
      remaining_pool: allocatable - totalRequested,
      corps_reserve: corpsReserve
    };
  }
  
  /**
   * Request strategic air evacuation (fixed-wing)
   * For patients requiring evacuation to Role 4
   * 
   * @param {Array} patients - Patients for strategic evac
   * @returns {Object} Request result
   */
  requestStrategicEvacuation(patients = []) {
    if (!this.strategic_evac.fixed_wing_available) {
      return {
        success: false,
        error: 'NO_FIXED_WING_AVAILABLE',
        alternative: 'GROUND_TO_ROLE4',
        patients_queued: patients.length
      };
    }
    
    this.strategic_evac.strategic_evac_pending += patients.length;
    
    return {
      success: true,
      mission_type: 'STRATEGIC_AEROMEDICAL',
      patients: patients.length,
      destination: 'ROLE4',
      estimated_departure: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString() // +4 hours
    };
  }
  
  /**
   * Add Role 2E to support list
   */
  addSupportedRole2E(role2eFacilityId) {
    if (!this.supported_role2es.includes(role2eFacilityId)) {
      this.supported_role2es.push(role2eFacilityId);
      this.addPickupSource(role2eFacilityId);
    }
  }
  
  /**
   * Get pull capacity for Role 2Es
   */
  getPullCapacityByRole2E(role2eDistances = {}) {
    const effective = this.getEffectiveAssets();
    
    // Role 3 uses more helicopters for longer distance pulls
    const helicopterAllocation = effective.helicopters.total * 0.6;  // 60% for pulls
    const groundAllocation = effective.ground_ambulances.total * 0.7; // 70% for pulls
    
    const numRole2Es = this.supported_role2es.length || 1;
    const helicoptersPerRole2E = Math.floor(helicopterAllocation / numRole2Es);
    const ambulancesPerRole2E = Math.floor(groundAllocation / numRole2Es);
    
    const capacityByRole2E = {};
    
    this.supported_role2es.forEach(role2eId => {
      const distance = role2eDistances[role2eId] || 40;
      const groundTrips = Math.max(1, 3 * (60 / distance));
      const heliTrips = Math.max(1, 6 * (80 / distance));
      
      capacityByRole2E[role2eId] = {
        ambulances_allocated: ambulancesPerRole2E,
        helicopters_allocated: helicoptersPerRole2E,
        distance_km: distance,
        ground_patients_per_day: Math.round(ambulancesPerRole2E * 4 * groundTrips),
        air_patients_per_day: Math.round(helicoptersPerRole2E * 6 * heliTrips)
      };
    });
    
    return {
      total_ambulances: groundAllocation,
      total_helicopters: helicopterAllocation,
      supported_role2es: this.supported_role2es.length,
      capacity_by_role2e: capacityByRole2E,
      total_daily_ground_capacity: Object.values(capacityByRole2E)
        .reduce((sum, c) => sum + c.ground_patients_per_day, 0),
      total_daily_air_capacity: Object.values(capacityByRole2E)
        .reduce((sum, c) => sum + c.air_patients_per_day, 0)
    };
  }
  
  // ============================================================================
  // ICU MANAGEMENT
  // ============================================================================
  
  admitToICU(patient, requiresVentilator = false) {
    if (this.icu_capability.icu_beds_occupied >= this.icu_capability.total_icu_beds) {
      return { success: false, reason: 'ICU_FULL', beds_available: 0 };
    }
    
    if (requiresVentilator && 
        this.icu_capability.ventilators_in_use >= this.icu_capability.ventilators) {
      return { success: false, reason: 'NO_VENTILATORS_AVAILABLE', ventilators_available: 0 };
    }
    
    this.icu_capability.icu_beds_occupied++;
    if (requiresVentilator) {
      this.icu_capability.ventilators_in_use++;
    }
    
    return {
      success: true,
      patient_id: patient.id,
      icu_bed_assigned: this.icu_capability.icu_beds_occupied,
      ventilator_assigned: requiresVentilator,
      timestamp: new Date()
    };
  }
  
  dischargeFromICU(patient, wasOnVentilator = false) {
    this.icu_capability.icu_beds_occupied = Math.max(0, this.icu_capability.icu_beds_occupied - 1);
    if (wasOnVentilator) {
      this.icu_capability.ventilators_in_use = Math.max(0, this.icu_capability.ventilators_in_use - 1);
    }
  }
  
  getICUStatus() {
    return {
      icu_beds_total: this.icu_capability.total_icu_beds,
      icu_beds_occupied: this.icu_capability.icu_beds_occupied,
      icu_beds_available: this.icu_capability.total_icu_beds - this.icu_capability.icu_beds_occupied,
      icu_occupancy_percent: ((this.icu_capability.icu_beds_occupied / this.icu_capability.total_icu_beds) * 100).toFixed(1),
      ventilators_total: this.icu_capability.ventilators,
      ventilators_in_use: this.icu_capability.ventilators_in_use,
      ventilators_available: this.icu_capability.ventilators - this.icu_capability.ventilators_in_use
    };
  }
  
  // ============================================================================
  // SURGICAL CAPACITY
  // ============================================================================
  
  canPerformSurgery() {
    return true;
  }
  
  canPerformDefinitiveSurgery() {
    return true;
  }
  
  performDefinitiveSurgery(patient, surgeryType = 'GENERAL') {
    if (this.surgical_capacity.surgeries_today >= this.surgical_capacity.max_surgeries_per_day) {
      return {
        success: false,
        reason: 'SURGICAL_CAPACITY_EXCEEDED',
        backlog: ++this.surgical_capacity.surgical_backlog
      };
    }
    
    const specializedTeams = this.surgical_capacity.specialized_teams;
    if (surgeryType !== 'GENERAL' && !specializedTeams[surgeryType.toLowerCase()]) {
      return {
        success: false,
        reason: 'SPECIALIZED_TEAM_NOT_AVAILABLE',
        available_types: Object.keys(specializedTeams)
      };
    }
    
    this.surgical_capacity.surgeries_today++;
    this.stats.total_surgeries_performed++;
    
    return {
      success: true,
      surgery_type: surgeryType,
      patient_id: patient.id,
      timestamp: new Date(),
      expected_recovery_time_hours: surgeryType === 'NEUROSURGERY' ? 168 : 72
    };
  }
  
  getSurgicalCapacity() {
    return {
      total_teams: this.surgical_teams,
      max_surgeries_per_day: this.surgical_capacity.max_surgeries_per_day,
      surgeries_today: this.surgical_capacity.surgeries_today,
      surgeries_remaining_today: this.surgical_capacity.max_surgeries_per_day - this.surgical_capacity.surgeries_today,
      surgical_backlog: this.surgical_capacity.surgical_backlog,
      specialized_teams: this.surgical_capacity.specialized_teams,
      can_perform_definitive_surgery: this.can_perform_definitive_surgery
    };
  }
  
  // ============================================================================
  // BLOOD BANK
  // ============================================================================
  
  activateMassiveTransfusionProtocol(patient) {
    const units_needed = { prbc: 6, ffp: 6, platelets: 6 };
    
    if (this.blood_bank.prbc_units < units_needed.prbc ||
        this.blood_bank.ffp_units < units_needed.ffp ||
        this.blood_bank.platelet_units < units_needed.platelets) {
      return {
        success: false,
        reason: 'INSUFFICIENT_BLOOD_PRODUCTS',
        available: {
          prbc: this.blood_bank.prbc_units,
          ffp: this.blood_bank.ffp_units,
          platelets: this.blood_bank.platelet_units
        },
        needed: units_needed
      };
    }
    
    this.blood_bank.prbc_units -= units_needed.prbc;
    this.blood_bank.ffp_units -= units_needed.ffp;
    this.blood_bank.platelet_units -= units_needed.platelets;
    
    return {
      success: true,
      patient_id: patient.id,
      units_issued: units_needed,
      timestamp: new Date(),
      protocol: 'MTP_1:1:1'
    };
  }
  
  getBloodBankStatus(daily_consumption = 80) {
    const days_supply = daily_consumption > 0 ? this.blood_bank.prbc_units / daily_consumption : 999;
    
    let alert_level = 'GREEN';
    if (days_supply < 0.5) alert_level = 'BLACK';
    else if (days_supply < 1) alert_level = 'RED';
    else if (days_supply < 3) alert_level = 'YELLOW';
    
    return {
      prbc_units: this.blood_bank.prbc_units,
      ffp_units: this.blood_bank.ffp_units,
      platelet_units: this.blood_bank.platelet_units,
      cryoprecipitate_units: this.blood_bank.cryoprecipitate_units,
      days_supply: days_supply,
      alert_level: alert_level,
      critical: alert_level === 'BLACK' || alert_level === 'RED',
      mtp_available: this.blood_bank.massive_transfusion_protocol_available
    };
  }
  
  resupplyBlood(units) {
    if (units.prbc_units) this.blood_bank.prbc_units += units.prbc_units;
    if (units.ffp_units) this.blood_bank.ffp_units += units.ffp_units;
    if (units.platelet_units) this.blood_bank.platelet_units += units.platelet_units;
    if (units.cryoprecipitate_units) this.blood_bank.cryoprecipitate_units += units.cryoprecipitate_units;
  }
  
  canHoldPatient(estimatedRecoveryHours) {
    return estimatedRecoveryHours <= this.hold_time_hours;
  }
  
  // ============================================================================
  // OVERRIDE METHODS
  // ============================================================================
  
  getOperationalIssues() {
    const issues = super.getOperationalIssues();
    
    if (this.surgical_capacity.backlog > 0) {
      issues.push(`Surgical backlog: ${this.surgical_capacity.backlog} patients`);
    }
    
    const blood_status = this.getBloodBankStatus();
    if (blood_status.critical) {
      issues.push(`Critical blood shortage (${this.blood_bank.prbc_units} units)`);
    }
    
    const icu_status = this.getICUStatus();
    if (parseFloat(icu_status.icu_occupancy_percent) > 85) {
      issues.push(`ICU near capacity (${icu_status.icu_occupancy_percent}%)`);
    }
    
    if (this.strategic_evac.strategic_evac_pending > 10) {
      issues.push(`Strategic evac backlog: ${this.strategic_evac.strategic_evac_pending} patients`);
    }
    
    const effective = this.getEffectiveAssets();
    if (effective.helicopters.total < 5) {
      issues.push('Critically low helicopter availability');
    }
    
    return issues;
  }
  
  toJSON() {
    return {
      ...super.toJSON(),
      parent_corps_id: this.parent_corps_id,
      national_standard: this.national_standard,
      hold_time_hours: this.hold_time_hours,
      can_perform_surgery: this.can_perform_surgery,
      can_perform_definitive_surgery: this.can_perform_definitive_surgery,
      specialized_care: this.specialized_care,
      surgical_capacity: this.getSurgicalCapacity(),
      icu_status: this.getICUStatus(),
      blood_bank_status: this.getBloodBankStatus(),
      imaging: this.imaging,
      laboratory: this.laboratory,
      supported_role2es: this.supported_role2es,
      helicopter_pool_status: this.getHelicopterPoolStatus(),
      pull_capacity: this.getPullCapacityByRole2E(),
      strategic_evac: this.strategic_evac
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// Already exported at definition

export default Role3Facility;
